/**
 * Schema helpers — universal fields for every Firebase document.
 * Used by DataService for all CRUD operations.
 *
 * CREATEDAT-2-PHASE-2 (2026-05-09): Migrated from Firestore Timestamp.now() to
 * ISO 8601 string convention. All timestamp fields are now plain ISO strings.
 * Consumers that read legacy Timestamp objects must normalize via timestampToIsoString()
 * in src/utils/timestamps.js (CREATEDAT-1 Phase 1 normalizer).
 */
import { DEMO_TENANT_ID } from '../data/tenantDefaults';

// ISO 8601 string — canonical timestamp convention (CREATEDAT-2)
const now = () => new Date().toISOString();

export function createDocument(data, userId = 'system', source = 'manual') {
  // SEAT-AUTH-GATE-TENANTID-HARDEN-1 (2026-05-21): AUDIT-1 P0 #4.
  // Silent fallback to DEMO_TENANT_ID masks tenant-isolation breaches across
  // every call site. Throw instead so real bugs surface at write time.
  if (!data.tenantId || typeof data.tenantId !== 'string' || data.tenantId.trim() === '') {
    throw new Error(
      '[SEAT-AUTH-GATE-TENANTID-HARDEN-1] createDocument requires a non-empty tenantId. ' +
      'Caller must provide data.tenantId — silent fallback to DEMO_TENANT_ID is forbidden.'
    );
  }
  return {
    ...data,
    tenantId: data.tenantId,
    customerId: data.customerId ?? null,
    loadId: data.loadId ?? null,
    quoteId: data.quoteId ?? null,
    createdAt: now(),
    createdBy: userId,
    updatedAt: now(),
    updatedBy: userId,
    deletedAt: null,
    deletedBy: null,
    isDeleted: false,
    version: 1,
    source,
  };
}

export function updateDocument(data, userId = 'system') {
  return {
    ...data,
    updatedAt: now(),
    updatedBy: userId,
  };
}

export function softDelete(userId = 'system') {
  return {
    isDeleted: true,
    deletedAt: now(),
    deletedBy: userId,
    updatedAt: now(),
    updatedBy: userId,
  };
}

export function activeQuery() {
  return { isDeleted: false };
}

export const LOAD_STATUSES = ['created', 'tendered', 'confirmed', 'picking_up', 'in_transit', 'delivered', 'closed', 'cancelled'];
export const QUOTE_STATUSES = ['draft', 'sent', 'viewed', 'approved', 'booked', 'expired', 'declined'];
export const FREIGHT_MODES = ['OTR', 'LTL', 'Intermodal', 'Drayage', 'Ocean', 'Air', 'Parcel'];
export const ALERT_LEVELS = ['normal', 'warning', 'breach', 'critical'];
export const APPOINTMENT_STATUSES = ['draft', 'lpns_pending', 'confirmed', 'checked_in', 'in_progress', 'completed', 'cancelled', 'no_show'];
export const USER_ROLES = ['watchmen_admin', 'watchmen_ops', 'customer_admin', 'location_user', 'carrier_user'];
